import * as React from 'react'
import { Route, Routes } from 'react-router-dom'

import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { UniversalImporterProcessDocument } from 'generated/resources/graphql'

import buildUrl from 'modules/core/lib/buildUrl'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { NavigationRoutesFn } from 'modules/core/lib/react-router'

import { getSettingPageMeta } from 'modules/settings/lib/getSettingPageMeta'
import {
  getImportTypename,
  mapStatus,
  mapStatusToVariant,
  percentageLabel,
} from 'modules/universal_importer/common/helpers'

const RootPage = React.lazy(() => import('modules/universal_importer/pages/settings/RootPage'))

const SettingsPage = React.lazy(
  () => import('modules/universal_importer/pages/settings/SettingsPage')
)

const UniversalImporterProcessPage = React.lazy(
  () => import('modules/universal_importer/pages/settings/UniversalImporterProcessPage')
)

const settingsRoutes: NavigationRoutesFn = (navigation) => {
  return [
    {
      path: navigation.settings.universalImporter.path,
      element: <RootPage />,
      handle: {
        resource: true,
        meta: () => {
          const settingsPageMeta = getSettingPageMeta(navigation, 'universal_importer')

          return {
            title: settingsPageMeta?.title,
            description: settingsPageMeta?.description,
            headerComponent: (
              <ResourceHeader
                title={settingsPageMeta?.title || ''}
                description={settingsPageMeta?.description || ''}
              />
            ),
          }
        },
      },
      children: [
        {
          index: true,
          element: <SettingsPage />,
        },
        {
          path: navigation.settings.universalImporter.universalImporterProcess.path,
          element: (
            <Routes>
              <Route path="" element={<UniversalImporterProcessPage />} />
            </Routes>
          ),
          handle: {
            resource: true,
            meta: ({ watchQuery, params }) => {
              return watchQuery({
                query: UniversalImporterProcessDocument,
                variables: {
                  importProcessId: Number(params.importProcessId),
                },
              }).map(({ data }) => {
                const importProcess = data?.universalImporter.importProcess
                const access = importProcess?.access

                const importTypename = getImportTypename(
                  importProcess?.importType ?? '',
                  importProcess?.legalEntity?.legalName ?? ''
                )

                return {
                  headerComponentProps: {
                    title: importTypename,
                    metadata: [
                      {
                        value: {
                          label: mapStatus(importProcess?.status ?? '').label,
                          type: 'status',
                          variant: mapStatusToVariant(importProcess?.status ?? ''),
                        },
                      },
                      {
                        label: i18n.t(
                          'universal_importer.import_history_process.resource_header.progress'
                        ),
                        value: {
                          content: percentageLabel(
                            importProcess?.processed ?? 0,
                            importProcess?.total ?? 0
                          ),
                          type: 'text',
                        },
                      },
                      {
                        label: i18n.t(
                          'universal_importer.import_history_process.resource_header.imported_by'
                        ),
                        value: {
                          text: access
                            ? `${access.firstName} ${access.lastName}`
                            : '',
                          type: 'avatar',
                          variant: {
                            firstName: access?.firstName ?? '',
                            lastName: access?.lastName ?? '',
                            src: buildUrl(access?.avatarFull ?? ''),
                            type: 'person',
                          },
                        },
                      },
                      {
                        label: i18n.t(
                          'universal_importer.import_history_process.resource_header.import_date'
                        ),
                        value: {
                          formattedDate: dateBackendDateTimeToUI(importProcess?.updatedAt ?? ''),
                          type: 'date',
                        },
                      },
                    ],
                  },
                  title: importTypename,
                }
              })
            },
          },
        },
      ],
    },
  ]
}

export default settingsRoutes
